import {
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    Paper,
    Stack,
    Typography,
    Tab,
    Tabs,
    TextField,
    MenuItem,
    Select,
    FormControl,
    Switch,
    FormControlLabel
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { OpenModalHookType } from '@hooks/useOpenModal';
import { hideLoader, showLoader } from '@utils/Loader';
import { showAlertError } from '@utils/Alerts/Error';
import { showAlertMessage } from '@utils/Alerts/Message';
import { Order, OrderStatus, orderStatusTranslations, DeliveryType } from '@api/Orders/Order';
import { buildOrderPayments, getOrderPaidBy, PaymentMethod } from '@api/Orders/OrderPayments';
import { isClosedStatus } from '@api/Orders/OrderPaymentGate';
import { Timestamp } from 'firebase/firestore';
import { format } from 'date-fns';
import { toDateSafe } from '@utils/Date';
import { currencyFormatter } from '@utils/Numbers';
import { getOrderLineDisplay } from '@utils/Order';
import OrdersService from '@api/Orders/OrderCtrl';
import UberCtrl from '@api/Uber/UberCtrl';
import IconButton from '@mui/material/IconButton';
import {
    ChevronLeft,
    ChevronRight,
    Note,
    LocalTaxi,
    Cancel,
    Edit,
    Save,
    Close,
    EventAvailable
} from '@mui/icons-material';
import { BranchOffice } from '@api/BranchOffices/BranchOffice';
import OrderActivityButton from '@components/Orders/OrderActivityButton';
import OrderElapsedTime from '@components/Orders/OrderElapsedTime';
import OrderPurgeButton from '@components/Orders/OrderPurgeButton';
import { showIOSAlert } from '@utils/Alerts/Confirm';
import { canCurrentUser } from '@utils/Permissions';
import { Capability } from '@utils/Permissions/Capabilities';

interface ModalViewOrderProps {
    modalViewOrder: OpenModalHookType;
    order: Order;
    orders?: Order[];
    onClose?: () => void;
    allowEdit?: boolean; // Habilita la opción de editar
    branchOffices?: BranchOffice[]; // Para mostrar el nombre de la sucursal
    onDeleted?: () => void; // Refrescar la lista de origen tras eliminar
}

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

const TabPanel = (props: TabPanelProps) => {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`order-tabpanel-${index}`}
            aria-labelledby={`order-tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
        </div>
    );
};

/** Lo cobrado por medio, leído de `payments[]` (`getOrderPaidBy`): lo que el modal muestra y edita. */
interface PaidAmounts {
    cash: number;
    card: number;
    transfer: number;
}

const paidAmountsOf = (order?: Order): PaidAmounts => ({
    cash: getOrderPaidBy(order, PaymentMethod.Cash),
    card: getOrderPaidBy(order, PaymentMethod.CreditCard),
    transfer: getOrderPaidBy(order, PaymentMethod.BankTransfer)
});

const samePaidAmounts = (a: PaidAmounts, b: PaidAmounts): boolean =>
    Math.abs(a.cash - b.cash) < 0.005 && Math.abs(a.card - b.card) < 0.005 && Math.abs(a.transfer - b.transfer) < 0.005;

export const ModalViewOrder: React.FunctionComponent<ModalViewOrderProps> = (props: ModalViewOrderProps) => {
    const { modalViewOrder, order, orders, onClose, allowEdit = true, branchOffices, onDeleted } = props;
    // La bitácora ("Actividad"), el borrado ("Eliminar") y el reloj ("Tiempo")
    // son los MISMOS del detalle de `/orders`: viven en `@components/Orders`.
    const ordersService = useMemo(() => new OrdersService(), []);
    const uberCtrl = useMemo(() => new UberCtrl(), []);

    const [currentOrder, setCurrentOrder] = useState<Order | undefined>();
    const [editedOrder, setEditedOrder] = useState<Order | undefined>();
    const [isEditing, setIsEditing] = useState(false);
    // Los montos se editan APARTE del pedido, sacados de `payments[]`: el pedido ya no
    // tiene por qué traer los 3 escalares viejos (F6 del plan de pagos).
    const [paymentDraft, setPaymentDraft] = useState<PaidAmounts>(paidAmountsOf());
    const [currentOrderIndex, setCurrentOrderIndex] = useState(0);
    const [tabValue, setTabValue] = useState(0);

    // --- LÓGICA DE CANCELACIÓN DE UBER ---
    const onCancelUberDelivery = async () => {
        if (!currentOrder?.uberDeliveryId) return;
        if (window.confirm(`¿Estás seguro de cancelar el viaje de Uber para la orden ${currentOrder.shortId}?`)) {
            showLoader();
            try {
                await uberCtrl.cancelDelivery(currentOrder.uberDeliveryId);
                showAlertMessage('Envío de Uber cancelado. (El estado se actualizará en breve).');
                handleClose();
            } catch (error: any) {
                showAlertError(error);
            }
            hideLoader();
        }
    };

    const getStatusColor = (status: OrderStatus) => {
        switch (status) {
            case OrderStatus.Completed:
                return 'success';
            case OrderStatus.Cancelled:
                return 'error';
            case OrderStatus.Paid:
                return 'success';
            case OrderStatus.Ready:
                return 'success';
            case OrderStatus.Route:
                return 'warning';
            case OrderStatus.WaitingForPayment:
                return 'warning';
            default:
                return 'warning';
        }
    };

    // ⚠️ Exigía `instanceof Timestamp`, así que una fecha que no viniera del SDK
    // (un Timestamp crudo, un ISO) se pintaba como "Fecha inválida" aunque fuera
    // perfectamente legible. `toDateSafe` entiende las cuatro formas.
    const formatDate = (date: any) => {
        const parsed = toDateSafe(date);
        if (!parsed) return date ? 'Fecha inválida' : 'N/A';
        return format(parsed, 'dd/MM/yyyy HH:mm');
    };

    const getBranchName = (branchOfficeId?: string): string => {
        if (!branchOfficeId) return 'N/A';
        return branchOffices?.find((b) => b.id === branchOfficeId)?.name || branchOfficeId;
    };

    // Valor para inputs type="datetime-local" (yyyy-MM-ddTHH:mm) desde Timestamp/Date
    const toDatetimeLocalValue = (date: any): string => {
        const parsed = toDateSafe(date);
        return parsed ? format(parsed, "yyyy-MM-dd'T'HH:mm") : '';
    };

    // Control de Edición
    const handleFieldChange = (field: keyof Order, value: any) => {
        if (editedOrder) {
            setEditedOrder({ ...editedOrder, [field]: value });
        }
    };

    // Toggle de reserva coherente: al apagarlo se limpia también reservationDate
    // (evita "reservas fantasma" — reservationDate colgando sin isReservation).
    const handleReservationToggle = (checked: boolean) => {
        if (!editedOrder) return;
        setEditedOrder({
            ...editedOrder,
            isReservation: checked || undefined,
            reservationDate: checked ? editedOrder.reservationDate : undefined
        });
    };

    // Edición de campos de fecha: convierte el string del input a Timestamp de Firestore.
    // Vacío = se elimina el campo (salvo `date`, que es obligatorio y se conserva).
    const handleDateFieldChange = (field: 'date' | 'pickupDate' | 'reservationDate', value: string) => {
        if (!editedOrder) return;
        if (!value) {
            if (field === 'date') return;
            setEditedOrder({ ...editedOrder, [field]: undefined });
            return;
        }
        const parsed = new Date(value);
        if (isNaN(parsed.getTime())) return;
        setEditedOrder({ ...editedOrder, [field]: Timestamp.fromDate(parsed) });
    };

    const onConfirmSaveOrder = async () => {
        try {
            showIOSAlert({
                title: 'Guardar cambios',
                message: '¿Estás seguro que deseas actualizar esta orden con los nuevos datos?',
                buttons: [
                    { text: 'Cancelar', style: 'cancel', onPress: () => {} },
                    {
                        text: 'Guardar',
                        onPress: async () => {
                            showLoader();
                            try {
                                if (editedOrder) {
                                    // `payments[]` se REARMA solo si el admin cambió un
                                    // monto. Antes se rearmaba en CUALQUIER guardado desde
                                    // los escalares: editar la fecha borraba la caja y el
                                    // cobrador de cada pago, y en un pedido sin escalares
                                    // lo dejaba en cero. La fecha del pago es la de la
                                    // orden: esto corrige un importe, no registra un cobro.
                                    const amountsChanged = !samePaidAmounts(paymentDraft, paidAmountsOf(currentOrder));
                                    const savedOrder: Order = amountsChanged
                                        ? {
                                              ...editedOrder,
                                              payments: buildOrderPayments({
                                                  cashAmount: paymentDraft.cash,
                                                  creditCardAmount: paymentDraft.card,
                                                  bankTransferAmount: paymentDraft.transfer,
                                                  date: editedOrder.date,
                                                  collaboratorId: editedOrder.collaboratorId
                                              })
                                          }
                                        : editedOrder;
                                    // El pedido ANTES de la edición: la bitácora
                                    // registra el antes → después de cada campo.
                                    await ordersService.updateOrder(savedOrder, currentOrder);
                                    setCurrentOrder(savedOrder);
                                    setIsEditing(false);
                                    showAlertMessage('Orden actualizada correctamente');
                                }
                            } catch (err: any) {
                                showAlertError(err);
                            }
                            hideLoader();
                        }
                    }
                ]
            });
        } catch (error: any) {
            showAlertError(error);
        }
    };

    // Control de paginación de órdenes
    const goToPreviousOrder = () => {
        if (!orders || orders.length === 0) return;
        const newIndex = currentOrderIndex > 0 ? currentOrderIndex - 1 : orders.length - 1;
        changeOrderFocus(orders[newIndex], newIndex);
    };

    const goToNextOrder = () => {
        if (!orders || orders.length === 0) return;
        const newIndex = currentOrderIndex < orders.length - 1 ? currentOrderIndex + 1 : 0;
        changeOrderFocus(orders[newIndex], newIndex);
    };

    const changeOrderFocus = (newOrder: Order, index: number) => {
        setCurrentOrderIndex(index);
        setCurrentOrder(newOrder);
        setEditedOrder(newOrder);
        setIsEditing(false);
        setTabValue(0);
    };

    const handleClose = () => {
        modalViewOrder.toggle();
        setIsEditing(false);
        onClose?.();
    };

    useEffect(() => {
        if (modalViewOrder.modalOpen && orders && orders.length > 0) {
            const index = orders.findIndex((o) => o.id === order.id);
            const foundIndex = index >= 0 ? index : 0;
            setCurrentOrderIndex(foundIndex);
            setCurrentOrder(order);
            setEditedOrder(order);
        } else if (modalViewOrder.modalOpen) {
            setCurrentOrder(order);
            setEditedOrder(order);
        }
    }, [modalViewOrder.modalOpen, order, orders]);

    const activeOrder = isEditing ? editedOrder : currentOrder;

    return (
        <Dialog
            PaperProps={{ style: { borderRadius: 16, padding: 8, minHeight: '85vh', backgroundColor: '#fcfcfc' } }}
            open={modalViewOrder.modalOpen}
            onClose={handleClose}
            fullWidth
            maxWidth="lg"
        >
            <DialogTitle sx={{ pb: 1 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Stack flex={1} direction="row" justifyContent="space-between" alignItems="center" gap={1}>
                        <Stack direction="row" alignItems="center">
                            {orders && orders.length > 0 && (
                                <IconButton onClick={goToPreviousOrder} size="small" disabled={isEditing}>
                                    <ChevronLeft />
                                </IconButton>
                            )}
                            <Stack alignItems="center" px={2}>
                                <Typography variant="h5" fontWeight={700} color="primary.main">
                                    Orden #{activeOrder?.shortId}
                                </Typography>
                                {orders && orders.length > 0 && (
                                    <Typography variant="caption" color="textSecondary">
                                        ({currentOrderIndex + 1} de {orders.length})
                                    </Typography>
                                )}
                            </Stack>
                            {orders && orders.length > 0 && (
                                <IconButton onClick={goToNextOrder} size="small" disabled={isEditing}>
                                    <ChevronRight />
                                </IconButton>
                            )}
                        </Stack>

                        <Stack direction="row" spacing={1}>
                            {!isEditing && <OrderActivityButton order={currentOrder} />}
                            {allowEdit && !isEditing && (
                                <Button
                                    startIcon={<Edit />}
                                    variant="outlined"
                                    onClick={() => {
                                        setPaymentDraft(paidAmountsOf(currentOrder));
                                        setIsEditing(true);
                                    }}
                                >
                                    Editar Orden
                                </Button>
                            )}
                            {/* Hard-delete con limpieza de efectos (caja, mesa, puntos) —
                                los dos admins, el de sucursal en las suyas (el gate real está en
                                `purgeOrder`). Aquí va el aviso largo (`detailed`). */}
                            {allowEdit &&
                                !isEditing &&
                                canCurrentUser(Capability.OrdersPurge, {
                                    branchOfficeId: currentOrder?.branchOfficeId || 'sin-sucursal'
                                }) && (
                                <OrderPurgeButton
                                    order={currentOrder}
                                    detailed
                                    onPurged={() => {
                                        handleClose();
                                        onDeleted?.();
                                    }}
                                />
                            )}
                            {isEditing && (
                                <>
                                    <Button
                                        startIcon={<Close />}
                                        color="error"
                                        onClick={() => {
                                            setIsEditing(false);
                                            setEditedOrder(currentOrder);
                                        }}
                                    >
                                        Cancelar
                                    </Button>
                                    <Button
                                        startIcon={<Save />}
                                        variant="contained"
                                        color="primary"
                                        onClick={onConfirmSaveOrder}
                                    >
                                        Guardar
                                    </Button>
                                </>
                            )}
                        </Stack>
                    </Stack>
                </Box>
            </DialogTitle>

            <DialogContent>
                {activeOrder && (
                    <Box>
                        {/* Cabecera de KPI's Rápidos */}
                        <Grid container spacing={2} sx={{ mb: 3 }}>
                            <Grid item xs={12} sm={6} md={3}>
                                <Paper
                                    elevation={0}
                                    sx={{ p: 2, textAlign: 'center', border: '1px solid #eee', borderRadius: 3 }}
                                >
                                    <Typography
                                        variant="caption"
                                        color="textSecondary"
                                        textTransform="uppercase"
                                        fontWeight={600}
                                    >
                                        Estado
                                    </Typography>
                                    {isEditing ? (
                                        <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                                            {/* Completar y cancelar NO se eligen aquí: tienen sus botones
                                                (`closingStatusChangeError`, y `updateOrder` lo rechaza). */}
                                            <Select
                                                value={activeOrder.status}
                                                disabled={isClosedStatus(currentOrder?.status)}
                                                onChange={(e) => handleFieldChange('status', e.target.value)}
                                            >
                                                {Object.values(OrderStatus)
                                                    .filter(
                                                        (status) =>
                                                            !isClosedStatus(status) || status === activeOrder.status
                                                    )
                                                    .map((status) => (
                                                        <MenuItem key={status} value={status}>
                                                            {orderStatusTranslations[status] || status}
                                                        </MenuItem>
                                                    ))}
                                            </Select>
                                        </FormControl>
                                    ) : (
                                        <Box mt={1}>
                                            <Chip
                                                label={
                                                    orderStatusTranslations[activeOrder.status] || activeOrder.status
                                                }
                                                color={getStatusColor(activeOrder.status)}
                                                size="small"
                                            />
                                        </Box>
                                    )}
                                </Paper>
                            </Grid>

                            <Grid item xs={12} sm={6} md={3}>
                                <Paper
                                    elevation={0}
                                    sx={{ p: 2, textAlign: 'center', border: '1px solid #eee', borderRadius: 3 }}
                                >
                                    <Typography
                                        variant="caption"
                                        color="textSecondary"
                                        textTransform="uppercase"
                                        fontWeight={600}
                                    >
                                        Entrega
                                    </Typography>
                                    {isEditing ? (
                                        <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                                            <Select
                                                value={activeOrder.deliveryType || ''}
                                                onChange={(e) => handleFieldChange('deliveryType', e.target.value)}
                                            >
                                                <MenuItem value={DeliveryType.Delivery}>Delivery</MenuItem>
                                                <MenuItem value={DeliveryType.Pickup}>Pikcup</MenuItem>
                                            </Select>
                                        </FormControl>
                                    ) : (
                                        <Typography variant="body1" fontWeight={600} sx={{ mt: 1 }}>
                                            {activeOrder.deliveryType === DeliveryType.Delivery ? 'Delivery' : 'Pikcup'}
                                        </Typography>
                                    )}
                                </Paper>
                            </Grid>

                            <Grid item xs={12} sm={6} md={3}>
                                <Paper
                                    elevation={0}
                                    sx={{ p: 2, textAlign: 'center', border: '1px solid #eee', borderRadius: 3 }}
                                >
                                    <Typography
                                        variant="caption"
                                        color="textSecondary"
                                        textTransform="uppercase"
                                        fontWeight={600}
                                    >
                                        Total
                                    </Typography>
                                    <Typography variant="h6" color="primary.main" fontWeight={700} sx={{ mt: 0.5 }}>
                                        {currencyFormatter(activeOrder.total)}
                                    </Typography>
                                </Paper>
                            </Grid>

                            <Grid item xs={12} sm={6} md={3}>
                                <Paper
                                    elevation={0}
                                    sx={{ p: 2, textAlign: 'center', border: '1px solid #eee', borderRadius: 3 }}
                                >
                                    <Typography
                                        variant="caption"
                                        color="textSecondary"
                                        textTransform="uppercase"
                                        fontWeight={600}
                                    >
                                        Tiempo
                                    </Typography>
                                    <Typography variant="body1" fontWeight={600} sx={{ mt: 1 }}>
                                        {/* El reloj corre desde la fecha GUARDADA, no la que se está editando */}
                                        <OrderElapsedTime
                                            start={currentOrder?.date?.toDate()}
                                            status={activeOrder.status}
                                            duration={activeOrder.duration}
                                        />
                                    </Typography>
                                </Paper>
                            </Grid>
                        </Grid>

                        {/* TABS SIMPLIFICADAS */}
                        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                            <Tabs
                                value={tabValue}
                                onChange={(_, newValue) => setTabValue(newValue)}
                                textColor="primary"
                                indicatorColor="primary"
                            >
                                <Tab label="Resumen" sx={{ fontWeight: 600 }} />
                                <Tab label="Productos" sx={{ fontWeight: 600 }} />
                                <Tab label="Pagos y Desglose" sx={{ fontWeight: 600 }} />
                            </Tabs>
                        </Box>

                        {/* TAB 1: RESUMEN */}
                        <TabPanel value={tabValue} index={0}>
                            <Grid container spacing={3}>
                                <Grid item xs={12} md={6}>
                                    <Typography variant="subtitle1" fontWeight={700} gutterBottom color="textSecondary">
                                        INFORMACIÓN BÁSICA
                                    </Typography>
                                    <Paper elevation={0} sx={{ p: 2.5, border: '1px solid #eee', borderRadius: 3 }}>
                                        <Stack spacing={1.5}>
                                            {isEditing ? (
                                                <Stack spacing={0.5}>
                                                    <Typography variant="body2">
                                                        <strong>Fecha de orden:</strong>
                                                    </Typography>
                                                    <TextField
                                                        type="datetime-local"
                                                        size="small"
                                                        value={toDatetimeLocalValue(activeOrder.date)}
                                                        onChange={(e) => handleDateFieldChange('date', e.target.value)}
                                                    />
                                                    <Typography variant="caption" color="error">
                                                        Cambiar esta fecha mueve la orden en reportes de ventas y cortes
                                                        de caja.
                                                    </Typography>
                                                </Stack>
                                            ) : (
                                                <Typography variant="body2">
                                                    <strong>Fecha de orden:</strong> {formatDate(activeOrder.date)}
                                                </Typography>
                                            )}
                                            <Typography variant="body2">
                                                <strong>Sucursal:</strong> {getBranchName(activeOrder.branchOfficeId)}
                                            </Typography>
                                            <Typography variant="body2">
                                                <strong>Colaborador:</strong>{' '}
                                                {activeOrder.collaboratorData?.profile?.name || 'N/A'}
                                            </Typography>
                                            <Typography variant="body2">
                                                <strong>Canal de venta:</strong>{' '}
                                                {activeOrder.salesChannel?.name || 'N/A'}
                                            </Typography>
                                            {activeOrder.promotionalCode && (
                                                <Typography variant="body2" color="secondary">
                                                    <strong>Código Promo:</strong> {activeOrder.promotionalCode.code}
                                                </Typography>
                                            )}
                                        </Stack>
                                    </Paper>

                                    <Typography
                                        variant="subtitle1"
                                        fontWeight={700}
                                        gutterBottom
                                        color="textSecondary"
                                        sx={{ mt: 3 }}
                                    >
                                        NOTAS DE LA ORDEN
                                    </Typography>
                                    <Paper
                                        elevation={0}
                                        sx={{ p: 2.5, border: '1px solid #eee', borderRadius: 3, bgcolor: '#fffdf5' }}
                                    >
                                        {isEditing ? (
                                            <TextField
                                                fullWidth
                                                multiline
                                                rows={3}
                                                size="small"
                                                value={activeOrder.note || ''}
                                                onChange={(e) => handleFieldChange('note', e.target.value)}
                                                placeholder="Agregar una nota a la orden..."
                                            />
                                        ) : (
                                            <Typography variant="body2">
                                                {activeOrder.note || 'Sin notas especiales.'}
                                            </Typography>
                                        )}
                                    </Paper>
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <Typography variant="subtitle1" fontWeight={700} gutterBottom color="textSecondary">
                                        CLIENTE Y ENTREGA
                                    </Typography>
                                    <Paper elevation={0} sx={{ p: 2.5, border: '1px solid #eee', borderRadius: 3 }}>
                                        <Stack spacing={1.5}>
                                            <Typography variant="body2">
                                                <strong>Cliente:</strong>{' '}
                                                {activeOrder.customer?.profile?.name || 'Público General'}
                                            </Typography>
                                            <Typography variant="body2">
                                                <strong>Teléfono:</strong> {activeOrder.customer?.phone || 'N/A'}
                                            </Typography>

                                            <Divider sx={{ my: 1 }} />

                                            {activeOrder.deliveryType === DeliveryType.Delivery &&
                                            activeOrder.deliveryAddress ? (
                                                <>
                                                    <Typography variant="body2">
                                                        <strong>Dirección:</strong>{' '}
                                                        {activeOrder.deliveryAddress.address}
                                                    </Typography>
                                                    {activeOrder.routeUrl && !activeOrder.uberDeliveryId && (
                                                        <Button
                                                            variant="outlined"
                                                            size="small"
                                                            onClick={() => window.open(activeOrder.routeUrl, '_blank')}
                                                            sx={{ mt: 1 }}
                                                        >
                                                            Ver ruta en mapa
                                                        </Button>
                                                    )}
                                                </>
                                            ) : activeOrder.deliveryType === DeliveryType.Pickup ? (
                                                <Typography variant="body2">
                                                    <strong>Recoger el:</strong> {formatDate(activeOrder.pickupDate)}
                                                </Typography>
                                            ) : (
                                                <Typography variant="body2" color="textSecondary">
                                                    Pedido en mostrador
                                                </Typography>
                                            )}

                                            {/* Programación / reserva (Delivery) */}
                                            {!isEditing &&
                                                activeOrder.deliveryType === DeliveryType.Delivery &&
                                                (activeOrder.isReservation || activeOrder.reservationDate) && (
                                                    <>
                                                        <Divider sx={{ my: 1 }} />
                                                        <Stack direction="row" spacing={1} alignItems="center">
                                                            {activeOrder.isReservation && (
                                                                <Chip
                                                                    icon={<EventAvailable />}
                                                                    label="Reserva"
                                                                    color="success"
                                                                    size="small"
                                                                />
                                                            )}
                                                            {activeOrder.reservationDate && (
                                                                <Typography variant="body2">
                                                                    <strong>Agendada:</strong>{' '}
                                                                    {formatDate(activeOrder.reservationDate)}
                                                                </Typography>
                                                            )}
                                                        </Stack>
                                                    </>
                                                )}

                                            {isEditing && activeOrder.deliveryType === DeliveryType.Delivery && (
                                                <>
                                                    <Divider sx={{ my: 1 }} />
                                                    <FormControlLabel
                                                        control={
                                                            <Switch
                                                                checked={!!activeOrder.isReservation}
                                                                onChange={(e) =>
                                                                    handleReservationToggle(e.target.checked)
                                                                }
                                                            />
                                                        }
                                                        label="Pedido agendado (reserva)"
                                                    />
                                                    {activeOrder.isReservation && (
                                                        <TextField
                                                            type="datetime-local"
                                                            size="small"
                                                            label="Fecha y hora de la reserva"
                                                            InputLabelProps={{ shrink: true }}
                                                            value={toDatetimeLocalValue(activeOrder.reservationDate)}
                                                            onChange={(e) =>
                                                                handleDateFieldChange('reservationDate', e.target.value)
                                                            }
                                                        />
                                                    )}
                                                </>
                                            )}

                                            {/* Fecha de recolección editable (Pickup) */}
                                            {isEditing && activeOrder.deliveryType === DeliveryType.Pickup && (
                                                <>
                                                    <Divider sx={{ my: 1 }} />
                                                    <TextField
                                                        type="datetime-local"
                                                        size="small"
                                                        label="Fecha de recolección"
                                                        InputLabelProps={{ shrink: true }}
                                                        value={toDatetimeLocalValue(activeOrder.pickupDate)}
                                                        onChange={(e) =>
                                                            handleDateFieldChange('pickupDate', e.target.value)
                                                        }
                                                    />
                                                </>
                                            )}
                                        </Stack>
                                    </Paper>

                                    {/* UBER DIRECT (Si existe) */}
                                    {activeOrder.uberDeliveryId && (
                                        <Paper
                                            elevation={0}
                                            sx={{ p: 2.5, mt: 2, border: '2px solid #000', borderRadius: 3 }}
                                        >
                                            <Stack spacing={1.5}>
                                                <Stack direction="row" alignItems="center" spacing={1}>
                                                    <LocalTaxi />
                                                    <Typography variant="subtitle1" fontWeight={700}>
                                                        Envío con Uber Direct
                                                    </Typography>
                                                </Stack>
                                                <Typography variant="body2">
                                                    <strong>Delivery ID:</strong> {activeOrder.uberDeliveryId}
                                                </Typography>
                                                <Stack direction="row" spacing={2} mt={1}>
                                                    {activeOrder.uberRouteUrl && (
                                                        <Button
                                                            variant="contained"
                                                            size="small"
                                                            sx={{ bgcolor: 'black', '&:hover': { bgcolor: '#333' } }}
                                                            onClick={() =>
                                                                window.open(activeOrder.uberRouteUrl, '_blank')
                                                            }
                                                        >
                                                            Rastrear en Uber
                                                        </Button>
                                                    )}
                                                    {activeOrder.status !== OrderStatus.Completed &&
                                                        activeOrder.status !== OrderStatus.Cancelled && (
                                                            <Button
                                                                variant="outlined"
                                                                color="error"
                                                                size="small"
                                                                startIcon={<Cancel />}
                                                                onClick={onCancelUberDelivery}
                                                            >
                                                                Cancelar Envío
                                                            </Button>
                                                        )}
                                                </Stack>
                                            </Stack>
                                        </Paper>
                                    )}
                                </Grid>
                            </Grid>
                        </TabPanel>

                        {/* TAB 2: PRODUCTOS — renderer dual v1/v2 (@utils/Order getOrderLineDisplay) */}
                        <TabPanel value={tabValue} index={1}>
                            <Paper elevation={0} sx={{ p: 3, border: '1px solid #eee', borderRadius: 3 }}>
                                {activeOrder.products?.map((product, index) => {
                                    const line = getOrderLineDisplay(product);
                                    return (
                                        <Box key={index} sx={{ mb: index !== activeOrder.products.length - 1 ? 3 : 0 }}>
                                            <Stack direction="row" spacing={2} alignItems="baseline">
                                                <Chip
                                                    label={`${line.quantity}x`}
                                                    color={line.takeAway ? 'secondary' : 'primary'}
                                                    size="small"
                                                />
                                                <Typography variant="body1" fontWeight={600} flex={1}>
                                                    {line.name}
                                                    {line.takeAway && (
                                                        <Typography
                                                            component="span"
                                                            variant="caption"
                                                            color="secondary"
                                                            sx={{ ml: 1 }}
                                                        >
                                                            (Para llevar)
                                                        </Typography>
                                                    )}
                                                </Typography>
                                                <Typography variant="body1" fontWeight={600}>
                                                    {currencyFormatter(line.total)}
                                                </Typography>
                                            </Stack>

                                            <Box pl={6} mt={1}>
                                                {/* Modificadores (variables/extras v1 · modifiers v2) */}
                                                {line.modifiers.map((modifier, idx) => (
                                                    <Typography key={idx} variant="body2" color="text.secondary">
                                                        + {modifier.quantity}x {modifier.label}
                                                        {modifier.optionName ? ` (${modifier.optionName})` : ''}
                                                        {modifier.extraCostTotal > 0
                                                            ? ` · +${currencyFormatter(modifier.extraCostTotal)}`
                                                            : ''}
                                                    </Typography>
                                                ))}
                                                {/* Sin ingredientes */}
                                                {line.removedIngredients.map((ingredientName, idx) => (
                                                    <Typography
                                                        key={idx}
                                                        variant="body2"
                                                        color="error.main"
                                                        fontStyle="italic"
                                                    >
                                                        - Sin {ingredientName}
                                                    </Typography>
                                                ))}
                                                {/* Nota del producto */}
                                                {line.note && (
                                                    <Stack
                                                        direction="row"
                                                        spacing={1}
                                                        mt={1}
                                                        bgcolor="#FFF8E1"
                                                        p={1}
                                                        borderRadius={2}
                                                    >
                                                        <Note fontSize="small" color="warning" />
                                                        <Typography variant="body2" fontStyle="italic">
                                                            {line.note}
                                                        </Typography>
                                                    </Stack>
                                                )}
                                            </Box>
                                            {index !== activeOrder.products.length - 1 && <Divider sx={{ mt: 3 }} />}
                                        </Box>
                                    );
                                })}
                            </Paper>
                        </TabPanel>

                        {/* TAB 3: PAGOS Y DESGLOSE */}
                        <TabPanel value={tabValue} index={2}>
                            <Grid container spacing={3}>
                                {/* Desglose de Costos */}
                                <Grid item xs={12} md={6}>
                                    <Typography variant="subtitle1" fontWeight={700} gutterBottom color="textSecondary">
                                        DESGLOSE DE COBROS
                                    </Typography>
                                    <Paper elevation={0} sx={{ p: 3, border: '1px solid #eee', borderRadius: 3 }}>
                                        <Stack spacing={2}>
                                            <Box display="flex" justifyContent="space-between" alignItems="center">
                                                <Typography variant="body2">Subtotal:</Typography>
                                                {isEditing ? (
                                                    <TextField
                                                        type="number"
                                                        size="small"
                                                        sx={{ width: 120 }}
                                                        value={activeOrder.subtotal}
                                                        onChange={(e) =>
                                                            handleFieldChange('subtotal', Number(e.target.value))
                                                        }
                                                    />
                                                ) : (
                                                    <Typography>{currencyFormatter(activeOrder.subtotal)}</Typography>
                                                )}
                                            </Box>

                                            <Box display="flex" justifyContent="space-between" alignItems="center">
                                                <Typography variant="body2">Descuento / Promo:</Typography>
                                                {isEditing ? (
                                                    <TextField
                                                        type="number"
                                                        size="small"
                                                        sx={{ width: 120 }}
                                                        value={activeOrder.promotion}
                                                        onChange={(e) =>
                                                            handleFieldChange('promotion', Number(e.target.value))
                                                        }
                                                    />
                                                ) : (
                                                    <Typography color="error">
                                                        -{currencyFormatter(activeOrder.promotion)}
                                                    </Typography>
                                                )}
                                            </Box>

                                            <Box display="flex" justifyContent="space-between" alignItems="center">
                                                <Typography variant="body2">Costo de envío:</Typography>
                                                {isEditing ? (
                                                    <TextField
                                                        type="number"
                                                        size="small"
                                                        sx={{ width: 120 }}
                                                        value={activeOrder.shippingCost}
                                                        onChange={(e) =>
                                                            handleFieldChange('shippingCost', Number(e.target.value))
                                                        }
                                                    />
                                                ) : (
                                                    <Typography>
                                                        {currencyFormatter(activeOrder.shippingCost)}
                                                    </Typography>
                                                )}
                                            </Box>

                                            <Divider />

                                            <Box display="flex" justifyContent="space-between" alignItems="center">
                                                <Typography variant="subtitle1" fontWeight={700}>
                                                    Total a Cobrar:
                                                </Typography>
                                                {isEditing ? (
                                                    <TextField
                                                        type="number"
                                                        size="small"
                                                        sx={{ width: 120 }}
                                                        value={activeOrder.total}
                                                        onChange={(e) =>
                                                            handleFieldChange('total', Number(e.target.value))
                                                        }
                                                    />
                                                ) : (
                                                    <Typography variant="h6" color="primary.main" fontWeight={700}>
                                                        {currencyFormatter(activeOrder.total)}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Stack>
                                    </Paper>
                                </Grid>

                                {/* Métodos de Pago Aplicados */}
                                <Grid item xs={12} md={6}>
                                    <Typography variant="subtitle1" fontWeight={700} gutterBottom color="textSecondary">
                                        MÉTODOS DE PAGO
                                    </Typography>
                                    <Paper elevation={0} sx={{ p: 3, border: '1px solid #eee', borderRadius: 3 }}>
                                        <Stack spacing={2}>
                                            {/* Propina (D2): NO es venta — no suma al total; los montos de
                                                pago de abajo ya la incluyen. Solo lectura a propósito. */}
                                            {!!activeOrder.tipAmount && activeOrder.tipAmount > 0 && (
                                                <Box display="flex" justifyContent="space-between" alignItems="center">
                                                    <Typography variant="body2">
                                                        Propina (incluida en los pagos):
                                                    </Typography>
                                                    <Typography color="success.main" fontWeight={600}>
                                                        {currencyFormatter(activeOrder.tipAmount)}
                                                    </Typography>
                                                </Box>
                                            )}
                                            <Box display="flex" justifyContent="space-between" alignItems="center">
                                                <Typography variant="body2">Efectivo:</Typography>
                                                {isEditing ? (
                                                    <TextField
                                                        type="number"
                                                        size="small"
                                                        sx={{ width: 120 }}
                                                        value={paymentDraft.cash}
                                                        onChange={(e) =>
                                                            setPaymentDraft({ ...paymentDraft, cash: Number(e.target.value) })
                                                        }
                                                    />
                                                ) : (
                                                    <Typography>{currencyFormatter(paidAmountsOf(activeOrder).cash)}</Typography>
                                                )}
                                            </Box>

                                            <Box display="flex" justifyContent="space-between" alignItems="center">
                                                <Typography variant="body2">Tarjeta de Crédito:</Typography>
                                                {isEditing ? (
                                                    <TextField
                                                        type="number"
                                                        size="small"
                                                        sx={{ width: 120 }}
                                                        value={paymentDraft.card}
                                                        onChange={(e) =>
                                                            setPaymentDraft({ ...paymentDraft, card: Number(e.target.value) })
                                                        }
                                                    />
                                                ) : (
                                                    <Typography>
                                                        {currencyFormatter(paidAmountsOf(activeOrder).card)}
                                                    </Typography>
                                                )}
                                            </Box>

                                            <Box display="flex" justifyContent="space-between" alignItems="center">
                                                <Typography variant="body2">Transferencia:</Typography>
                                                {isEditing ? (
                                                    <TextField
                                                        type="number"
                                                        size="small"
                                                        sx={{ width: 120 }}
                                                        value={paymentDraft.transfer}
                                                        onChange={(e) =>
                                                            setPaymentDraft({
                                                                ...paymentDraft,
                                                                transfer: Number(e.target.value)
                                                            })
                                                        }
                                                    />
                                                ) : (
                                                    <Typography>
                                                        {currencyFormatter(paidAmountsOf(activeOrder).transfer)}
                                                    </Typography>
                                                )}
                                            </Box>

                                            <Box display="flex" justifyContent="space-between" alignItems="center">
                                                <Typography variant="body2">Puntos de Lealtad:</Typography>
                                                {isEditing ? (
                                                    <TextField
                                                        type="number"
                                                        size="small"
                                                        sx={{ width: 120 }}
                                                        value={activeOrder.pointsAmount}
                                                        onChange={(e) =>
                                                            handleFieldChange('pointsAmount', Number(e.target.value))
                                                        }
                                                    />
                                                ) : (
                                                    <Typography>
                                                        {currencyFormatter(activeOrder.pointsAmount)}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Stack>
                                    </Paper>

                                    {/* Info pasarela de pago (Stripe) */}
                                    {activeOrder.stripe && (
                                        <Box mt={3}>
                                            <Typography
                                                variant="subtitle2"
                                                fontWeight={700}
                                                gutterBottom
                                                color="textSecondary"
                                            >
                                                PASARELA ELECTRÓNICA
                                            </Typography>
                                            <Paper elevation={0} sx={{ p: 2, bgcolor: '#f4f6f8', borderRadius: 3 }}>
                                                <Typography variant="caption" display="block">
                                                    <strong>Status:</strong> {activeOrder.stripe.status}
                                                </Typography>
                                                <Typography variant="caption" display="block">
                                                    <strong>Payment ID:</strong> {activeOrder.stripe.paymentIntentId}
                                                </Typography>
                                            </Paper>
                                        </Box>
                                    )}
                                </Grid>
                            </Grid>
                        </TabPanel>
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{ p: 2, pt: 0 }}>
                <Button onClick={handleClose} variant="text" color="inherit" sx={{ fontWeight: 600 }}>
                    Cerrar Ventana
                </Button>
            </DialogActions>
        </Dialog>
    );
};
